import isEmpty from 'lodash.isempty';
import { gql } from '@apollo/client';

import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';

import {
  useUnqualifiedLicenseQuery,
  type UnqualifiedLicenseQuery,
} from './__graphql__/useUnqualifiedLicense';

type RequireProduct = NonNullable<
  NonNullable<
    NonNullable<UnqualifiedLicenseQuery['content']>['required_base_products']
  >[0]
>;

type RequireProductMessage = NonNullable<RequireProduct['messages']>[0];

export type UnqualifiedProducts = {
  productName: string;
  productSku: string;
  localizedMessage: RequireProductMessage | undefined;
  requiredBaseProductsTranslation: string;
}[];

type Result = {
  loading: boolean;
  hasUnqualifiedLicense: boolean;
  unqualifiedProducts: UnqualifiedProducts;
};

type Props = {
  products?: {
    name: string;
    sku: string;
    sdbsId: string;
  }[];
};

export const useUnqualifiedLicense = ({ products }: Props): Result => {
  const productsSkus = products?.map((product) => product.sku);
  const { findTranslationByKeyWithFallback, getLanguagesWithFallback } =
    useLocaleWithFallback();

  const userLanguage = getLanguagesWithFallback()[0];

  const { data, loading } = useUnqualifiedLicenseQuery({
    skip: isEmpty(productsSkus),
    variables: {
      skus: productsSkus,
      languages: getLanguagesWithFallback(),
    },
  });

  const upgradePathIds =
    data?.userV2?.__typename === 'AuthenticatedUser'
      ? data?.userV2?.upgradePaths
          ?.filter(({ upgrade_path }) => !isEmpty(upgrade_path))
          .flatMap(({ upgrade_path }) =>
            upgrade_path.map((_up) => _up?.licensedproduct_id),
          )
      : [];

  const qualifiedVariantsSkus = products
    ?.filter(({ sdbsId }) => upgradePathIds?.includes(Number(sdbsId)))
    .map(({ sku }) => sku);

  const requiredBaseProducts: RequireProduct[] =
    data?.content?.required_base_products
      .filter(({ sku }) => !qualifiedVariantsSkus?.includes(sku))
      // biome-ignore lint/suspicious/noSelfCompare: Please fix this code!
      .filter(({ messages }) => messages?.length ?? 0 > 0) ?? [];

  const unqualifiedProducts: UnqualifiedProducts =
    requiredBaseProducts?.map((requiredBaseProduct) => {
      const requiredBaseProductMessages =
        requiredBaseProduct?.messages && requiredBaseProduct?.messages;

      const localizedMessage = requiredBaseProductMessages?.find(
        (message) => message?.languages_code?.code === userLanguage,
      );

      const requiredBaseProductsTranslation = findTranslationByKeyWithFallback({
        translations: requiredBaseProductMessages,
        key: 'required_base_products',
      });

      const productName =
        products?.find((product) => product.sku === requiredBaseProduct?.sku)
          ?.name ?? '';

      return {
        localizedMessage,
        requiredBaseProductsTranslation,
        productName,
        productSku: requiredBaseProduct?.sku ?? '',
      };
    }) ?? [];

  return {
    loading,
    unqualifiedProducts,
    hasUnqualifiedLicense: !isEmpty(requiredBaseProducts),
  };
};

export const UNQUALIFIED_LICENSE_WARNING = gql`
  query UnqualifiedLicense($languages: [String], $skus: [String!]) {
    content {
      required_base_products(filter: { sku: { _in: $skus } }) {
        sku
        messages(filter: { languages_code: { code: { _in: $languages } } }) {
          id
          required_base_products
          languages_code {
            code
          }
        }
      }
    }
    userV2 {
      __typename
      ... on AuthenticatedUser {
        upgradePaths {
          upgrade_path {
            licensedproduct_id
          }
        }
      }
    }
  }
`;
